import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import ChatHeader from './components/ChatHeader';
import PopularQuestions from './components/PopularQuestions';
import ChatInput from './components/ChatInput';
import EnvBadge from './components/EnvBadge';
import { postToHost, readQueryParams, resolvePublishableKey } from './lib/bridge';
import { streamAvatarReply } from './lib/sseClient';
import type { BridgeMessage, ChatMessage } from './types/bridge';

const INIT_MESSAGE: ChatMessage = {
  id: 'init',
  text: 'Hello! How can I assist you today?',
  sender: 'avatar',
  status: 'complete',
};

const styles: Record<string, CSSProperties> = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: '100vh',
    background: '#F7F7F8',
    fontFamily:
      '"DM Sans", "Segoe UI", system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
    color: '#111827',
    position: 'relative',
  },
  body: {
    flex: 1,
    overflowY: 'auto',
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  bubbleUser: {
    alignSelf: 'flex-end',
    maxWidth: '85%',
    background: 'linear-gradient(135deg, #FF5F05 0%, #E84A00 100%)',
    color: '#FFFFFF',
    padding: '10px 14px',
    borderRadius: '14px 14px 4px 14px',
    fontSize: 14,
    lineHeight: 1.45,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  bubbleAvatar: {
    alignSelf: 'flex-start',
    maxWidth: '85%',
    background: '#FFFFFF',
    color: '#1F2937',
    padding: '10px 14px',
    borderRadius: '14px 14px 14px 4px',
    fontSize: 14,
    lineHeight: 1.45,
    border: '1px solid #E5E7EB',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  bubbleSystem: {
    alignSelf: 'center',
    maxWidth: '92%',
    background: '#FEF2F2',
    color: '#991B1B',
    padding: '10px 14px',
    borderRadius: 12,
    fontSize: 13,
    lineHeight: 1.4,
    textAlign: 'center',
    border: '1px solid #FECACA',
  },
  typing: {
    alignSelf: 'flex-start',
    fontSize: 12,
    color: '#9CA3AF',
    paddingLeft: 4,
  },
  retryBtn: {
    marginTop: 8,
    border: '1px solid #FCA5A5',
    background: '#FFFFFF',
    color: '#B91C1C',
    borderRadius: 8,
    padding: '6px 12px',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
};

function createId(prefix: string): string {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

function shouldAutoOpen(): boolean {
  const isEmbedded = window.parent !== window;
  const preview = new URLSearchParams(window.location.search).has('preview');
  return !isEmbedded || preview;
}

export default function App() {
  const { publishableKey } = readQueryParams();
  const autoOpen = shouldAutoOpen();
  const [isOpen, setIsOpen] = useState(autoOpen);
  const [messages, setMessages] = useState<ChatMessage[]>(() =>
    autoOpen ? [{ ...INIT_MESSAGE }] : [],
  );
  const [isStreaming, setIsStreaming] = useState(false);
  const [serviceUnavailable, setServiceUnavailable] = useState(false);
  const [lastFailedText, setLastFailedText] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const pkRef = useRef(publishableKey);

  const purgeConversation = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setMessages([]);
    setIsStreaming(false);
    setServiceUnavailable(false);
    setLastFailedText(null);
  }, []);

  const startFreshConversation = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setMessages([{ ...INIT_MESSAGE }]);
    setIsStreaming(false);
    setServiceUnavailable(false);
    setLastFailedText(null);
  }, []);

  useEffect(() => {
    const handleBridgeMessages = (event: MessageEvent) => {
      const data = event.data as BridgeMessage;
      if (!data || data.source !== 'avatar_host_script') return;

      if (data.event === 'WIDGET_OPEN') {
        const key = resolvePublishableKey(data.payload);
        if (key) pkRef.current = key;
        setIsOpen(true);
        startFreshConversation();
      }

      if (data.event === 'WIDGET_CLOSE') {
        setIsOpen(false);
        purgeConversation(); // PRIVACY: zero state persisted
      }
    };

    window.addEventListener('message', handleBridgeMessages);

    // Tell host the panel is ready so it can (re)send WIDGET_OPEN after lazy iframe load.
    if (window.parent !== window) {
      postToHost('WIDGET_READY');
    }

    return () => {
      window.removeEventListener('message', handleBridgeMessages);
      abortRef.current?.abort();
    };
  }, [purgeConversation, startFreshConversation]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, isStreaming]);

  const sendMessage = useCallback(async (text: string) => {
    if (serviceUnavailable || isStreaming) return;

    const userMsg: ChatMessage = {
      id: createId('user'),
      text,
      sender: 'user',
      status: 'complete',
    };
    const streamId = createId('avatar');
    const placeholder: ChatMessage = {
      id: streamId,
      text: '',
      sender: 'avatar',
      status: 'streaming',
    };

    setMessages((prev) => [...prev, userMsg, placeholder]);
    setIsStreaming(true);
    setLastFailedText(null);

    const controller = new AbortController();
    abortRef.current = controller;

    await streamAvatarReply({
      publishableKey: pkRef.current,
      message: text,
      signal: controller.signal,
      onChunk: (chunk) => {
        if (chunk.type === 'token' && chunk.text) {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === streamId ? { ...m, text: m.text + chunk.text } : m,
            ),
          );
        }

        if (chunk.type === 'done') {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === streamId ? { ...m, status: 'complete' } : m,
            ),
          );
          setIsStreaming(false);
        }

        if (chunk.type === 'error') {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === streamId
                ? {
                    ...m,
                    sender: 'system',
                    status: 'error',
                    text:
                      chunk.reason ||
                      'Something went wrong. Please try again.',
                  }
                : m,
            ),
          );
          setLastFailedText(text);
          setIsStreaming(false);
          postToHost('API_STREAM_ERROR', { reason: chunk.reason });
        }
      },
    });
  }, [isStreaming, serviceUnavailable]);

  const handleReset = () => {
    if (isStreaming) return;
    startFreshConversation();
  };

  const showPopular = messages.length <= 1 && !isStreaming;

  if (!isOpen) return null;

  return (
    <div style={styles.root}>
      <EnvBadge />
      <ChatHeader onReset={handleReset} disabled={isStreaming} />
      <div ref={scrollRef} style={styles.body}>
        {showPopular && (
          <PopularQuestions
            onSelectQuestion={sendMessage}
            disabled={serviceUnavailable || isStreaming}
          />
        )}

        {!showPopular &&
          messages.map((msg) => {
            if (msg.sender === 'system' || msg.status === 'error') {
              return (
                <div key={msg.id} style={styles.bubbleSystem}>
                  <div>{msg.text}</div>
                  {lastFailedText && msg.status === 'error' && (
                    <button
                      type="button"
                      style={styles.retryBtn}
                      onClick={() => sendMessage(lastFailedText)}
                    >
                      Try again
                    </button>
                  )}
                </div>
              );
            }

            // Hide the silent init greeting once the user has started chatting
            if (msg.id === 'init' && messages.length > 1) return null;

            return (
              <div
                key={msg.id}
                style={
                  msg.sender === 'user' ? styles.bubbleUser : styles.bubbleAvatar
                }
              >
                {msg.text || (msg.status === 'streaming' ? '…' : '')}
              </div>
            );
          })}

        {isStreaming && <div style={styles.typing}>Avatar is typing…</div>}
      </div>
      <ChatInput
        onSend={sendMessage}
        disabled={serviceUnavailable || isStreaming}
      />
    </div>
  );
}
